import StyledBreadcrumbs from "../../../components/StyledBreadcrumbs";
import { pagePathGenerator } from "../../../routes/pagePathGenerator";
import { useSelector } from "react-redux";
import {
  selectCurrentLocation,
  selectLocationById, selectLocationByMapId,
} from "../../../store/locationStore/locationSelectors";
import {
  selectBuildingById,
  selectCurrentBuilding,
} from "../../../store/buildingStore/buildingsSelectors";
import {
  selectCurrentFloor,
  selectFloorByMapId,
} from "../../../store/floorStore/floorSelectors";
import { selectCurrentDevice } from "../../../store/deviceStore/deviceSelectors";
import { selectCurrentMapId } from "../../../store/mapStore/mapSelectors";
import { selectCurrentLicence } from "../../../store/licenceStore/licenceSelectors";

enum BreadcrumbsItemType {
  HOME,
  LOCATION,
  BUILDING,
  FLOOR,
  DEVICE,
  DEVICE_MAP,
  TEMPLATE,
  LICENCE,
}

interface BreadcrumbsContainerProps {
  showLastItemLink?: boolean;
  getPathFromDevice?: boolean;
  items: BreadcrumbsItemType[];
}

const BreadcrumbsContainer = (props: BreadcrumbsContainerProps) => {
  const { items = [], showLastItemLink = false, getPathFromDevice = false } = props;
  const selectedLocation = useSelector(selectCurrentLocation);
  const selectedBuilding = useSelector(selectCurrentBuilding)
  const selectedFloor = useSelector(selectCurrentFloor)
  const currentDevice = useSelector(selectCurrentDevice);
  const currentMapId = useSelector(selectCurrentMapId);
  const currentLicence = useSelector(selectCurrentLicence);
  const mapId = currentDevice?.mapId || currentMapId;
  const floorByMapId = useSelector(selectFloorByMapId(mapId));
  const buildingByMapId = useSelector(selectBuildingById(floorByMapId?.buildingId));
  const locationByFloorMapId = useSelector(selectLocationById(buildingByMapId?.locationId));
  const locationByMapId = useSelector(selectLocationByMapId(mapId));

  const data = getPathFromDevice
    ? {
      currentLocation: locationByFloorMapId || locationByMapId,
      currentBuilding: buildingByMapId,
      currentFloor: floorByMapId,
      currentDevice,
    }
    : {
      currentLocation: selectedLocation,
      currentBuilding: selectedBuilding,
      currentFloor: selectedFloor,
      currentLicence,
      currentDevice,
    }

  const breadCrumbsItems = items.map((containerItemType, index) => {
    const hasLink = showLastItemLink || index < items.length - 1;
    const { currentLocation, currentBuilding, currentFloor, currentLicence, currentDevice } = data;

    switch (containerItemType) {
      case BreadcrumbsItemType.LOCATION:
        if (!currentLocation) {
          return {
            title: 'Home',
            link: hasLink ? pagePathGenerator.HOME() : '',
          }
        }
        return {
          title: currentLocation?.name,
          tooltip: currentLocation?.name,
          link: hasLink ? pagePathGenerator.LOCATION_PAGE({
            locationId: currentLocation?.id || 1,
          }) : '',
        }
      case BreadcrumbsItemType.BUILDING:
        if (!currentBuilding) {
          return null;
        }
        return {
          title: currentBuilding?.name,
          tooltip: currentBuilding?.name,
          link: hasLink ? pagePathGenerator.BUILDING_PAGE({
            locationId: currentLocation?.id || 1,
            buildingId: currentBuilding?.id || undefined,
          }) : '',
        }
      case BreadcrumbsItemType.FLOOR:
        if (!currentFloor) {
          return null;
        }
        return {
          title: currentFloor.name,
          tooltip: currentFloor.name,
          link: hasLink ? pagePathGenerator.BUILDING_PAGE({
            locationId: currentLocation?.id || 1,
            buildingId: currentBuilding?.id || undefined,
            floorId: currentFloor?.id || undefined,
          }) : '',
        }
      case BreadcrumbsItemType.DEVICE:
        if (!currentDevice) {
          return null;
        }
        return {
          title: currentDevice?.name,
          tooltip: currentDevice?.name,
          link: hasLink ? pagePathGenerator.SINGLE_DEVICE_PAGE({
            mapId: currentDevice?.mapId || 1,
            deviceId: currentDevice?.id || undefined,
          }) : '',
        }
      case BreadcrumbsItemType.DEVICE_MAP:
        return {
          title: 'Device map',
          link: '',
        }
      case BreadcrumbsItemType.LICENCE:
        return {
          title: currentLicence?.name || 'New licence',
          link: '',
        }
      default:
        return {
          title: 'Home',
          link: hasLink ? pagePathGenerator.HOME() : '',
        }
    }
  });

  return <StyledBreadcrumbs items={breadCrumbsItems} />
}

BreadcrumbsContainer.TYPES = BreadcrumbsItemType;

export default BreadcrumbsContainer;